import { Alert, Text } from '@amzn/meridian';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { sizes } from '../../resources/tokens';
import { transformError } from '../../utils/errorTransformation';
import type { DelayedPermissionsErrorConfig } from './ErrorAlert.types';
import { MultipleErrorDisplay } from './MultipleErrorDisplay';
import { SingleErrorDisplay } from './SingleErrorDisplay';
import { useProcessedErrors } from './useProcessedErrors';

type ErrorAlertProps = {
  contextMessage?: string;
  delayedPermissionsError?: DelayedPermissionsErrorConfig;
  error?: unknown;
  errors?: unknown[];
};

const USER_GUIDANCE_MESSAGE = 'Please try again or cut a ticket to the Journeys team if the problem continues.';

function isPermissionsError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const err = error as Record<string, unknown>;
  if (err.status === 403 || err.statusCode === 403) return true;
  if (typeof err.extensions === 'object' && err.extensions !== null) {
    return (err.extensions as Record<string, unknown>).code === 'FORBIDDEN';
  }
  return err.code === 'FORBIDDEN';
}

/**
 * Displays a user-friendly error message in an Alert component with expandable error details.
 */
export const ErrorAlert: FC<ErrorAlertProps> = ({ contextMessage, delayedPermissionsError, error, errors }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showActualError, setShowActualError] = useState(!delayedPermissionsError || !isPermissionsError(error));
  const { processedErrors, hasAnyAdditionalLines, briefSummary, shouldIncludeBriefSummary } = useProcessedErrors(error, errors);

  useEffect(() => {
    if (delayedPermissionsError && isPermissionsError(error)) {
      const timer = setTimeout(() => setShowActualError(true), delayedPermissionsError.delayMs);
      return () => clearTimeout(timer);
    }
    setShowActualError(true);
  }, [
    error,
    delayedPermissionsError,
  ]);

  if (!showActualError && delayedPermissionsError) {
    return (
      <Alert type="warning">
        <Text>{delayedPermissionsError.warningMessage}</Text>
      </Alert>
    );
  }

  if (contextMessage) {
    return (
      <Alert type="error">
        <Text>
          {shouldIncludeBriefSummary
            ? `${contextMessage}: ${briefSummary}. ${USER_GUIDANCE_MESSAGE}`
            : `${contextMessage}. ${USER_GUIDANCE_MESSAGE}`}
        </Text>

        {processedErrors.length > 0 && (
          <div
            style={{
              marginTop: sizes.smallPx,
            }}
          >
            <Text>
              <strong>Error Details:</strong>
            </Text>

            {processedErrors.length === 1 ? (
              <SingleErrorDisplay
                isExpanded={isExpanded}
                onToggleExpanded={() => setIsExpanded(!isExpanded)}
                processedError={processedErrors[0]}
              />
            ) : (
              <MultipleErrorDisplay
                hasAnyAdditionalLines={hasAnyAdditionalLines}
                isExpanded={isExpanded}
                onToggleExpanded={() => setIsExpanded(!isExpanded)}
                processedErrors={processedErrors}
              />
            )}
          </div>
        )}
      </Alert>
    );
  }

  const message = error ? transformError(error) : 'An unknown error occurred';
  return <Alert type="error">{message}</Alert>;
};
